import { cn } from './cn';
import { SparkleGlyph } from './Sparkle';

/** The default cut-paper surface. */
export function Card({
  className,
  as: As = 'div',
  ...rest
}: React.HTMLAttributes<HTMLElement> & { as?: React.ElementType }) {
  return <As className={cn('u-card p-xl', className)} {...rest} />;
}

/**
 * A card with a torn-paper top edge. Used for story and gallery cards so the
 * artwork always sits on something that reads as paper rather than as chrome.
 */
export function TornCard({
  className,
  tint = 'card',
  children,
}: {
  className?: string;
  tint?: 'card' | 'primary' | 'accent' | 'secondary' | 'sunny';
  children: React.ReactNode;
}) {
  const tints = {
    card: 'bg-card',
    primary: 'bg-primary-soft',
    accent: 'bg-accent-soft',
    secondary: 'bg-secondary-soft',
    sunny: 'bg-sunny/25',
  } as const;
  return (
    <div className={cn('relative rounded-blob border-2 border-line shadow-cut', tints[tint], className)}>
      <svg
        aria-hidden
        viewBox="0 0 400 12"
        preserveAspectRatio="none"
        className="absolute -top-[2px] left-0 h-3 w-full text-line"
      >
        <path
          d="M0 12 L0 6 Q 20 0 40 5 T 80 5 T 120 4 T 160 6 T 200 3 T 240 6 T 280 4 T 320 6 T 360 4 T 400 6 L400 12 Z"
          fill="currentColor"
          opacity="0.5"
        />
      </svg>
      {children}
    </div>
  );
}

/** Section label with the sparkle mark. Keeps headings recognisable. */
export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'left',
  className,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  align?: 'left' | 'center';
  className?: string;
}) {
  return (
    <header className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
      {eyebrow ? (
        <p
          className={cn(
            'mb-s inline-flex items-center gap-xs rounded-full bg-primary-soft px-m py-[6px] font-display text-[13px] font-semibold uppercase tracking-[0.14em] text-primary-deep',
          )}
        >
          <SparkleGlyph className="h-3.5 w-3.5" />
          {eyebrow}
        </p>
      ) : null}
      <h2 className="font-display text-[28px] font-semibold leading-tight text-ink sm:text-[36px]">
        {title}
      </h2>
      {description ? (
        <p className="mt-m text-[17px] leading-relaxed text-ink-soft sm:text-[18px]">{description}</p>
      ) : null}
    </header>
  );
}

/** Small status pill. Always carries a word — never colour alone (spec §4.1). */
export function Chip({
  tone = 'neutral',
  icon,
  children,
  className,
}: {
  tone?: 'neutral' | 'magic' | 'accent' | 'leaf' | 'sunny' | 'secondary';
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const tones = {
    neutral: 'bg-paper-deep text-ink-soft border-line',
    magic: 'bg-magic/10 text-magic-deep border-magic/30',
    accent: 'bg-accent-soft text-accent border-accent/30',
    leaf: 'bg-leaf/15 text-leaf border-leaf/30',
    sunny: 'bg-sunny/20 text-ink border-sunny/40',
    secondary: 'bg-secondary-soft text-secondary border-secondary/30',
  } as const;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-xs whitespace-nowrap rounded-full border px-m py-[5px] font-display text-[13px] font-semibold',
        tones[tone],
        className,
      )}
    >
      {icon ? <span aria-hidden className="shrink-0">{icon}</span> : null}
      {children}
    </span>
  );
}

/**
 * Complete empty state with a next action — never a bare "nothing here"
 * (spec §4.2).
 */
export function EmptyState({
  art,
  title,
  body,
  action,
  className,
}: {
  art?: React.ReactNode;
  title: string;
  body?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-blob border-2 border-dashed border-line bg-paper-deep/50 px-xl py-3xl text-center',
        className,
      )}
    >
      <div aria-hidden className="mb-l text-magic/70">
        {art ?? <SparkleGlyph className="h-12 w-12 animate-float-y" />}
      </div>
      <h3 className="font-display text-[20px] font-semibold text-ink">{title}</h3>
      {body ? <p className="mt-s max-w-sm text-[16px] text-ink-soft">{body}</p> : null}
      {action ? <div className="mt-xl">{action}</div> : null}
    </div>
  );
}
